/**
 * The manager app shell.
 *
 * A fixed dark-green rail on the left, a white bar across the top, and content on a light
 * ground. The rail is dark because it is furniture — it should recede, and it gives the brand
 * colour somewhere to live that is not competing with the red and amber that carry meaning on
 * every screen.
 *
 * The one number in the navigation is the count of overdue invoices. It is there because the
 * product's promise is that an arrear is never a surprise, so it follows the manager onto every
 * screen rather than living only on the dashboard.
 *
 * **On a phone it is not the same shell made narrow.** A drawer behind a hamburger puts every
 * destination two taps away, at the top-left corner, which is the furthest point on the screen
 * from a thumb. So the phone gets a bottom bar with the five places people actually go, a raised
 * centre button for the thing this product is for — recording something — and the full rail still
 * behind "More" for everything else.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MODULE_INFO, MODULES, homeFor, moduleHome, withModule, type ModuleCode, type Vocabulary } from "@rentorng/core";
import type { Permission } from "@rentorng/core/permissions";
import { api } from "@/lib/api";
import { PageTransition } from "./page-transition";
import { Wordmark } from "./wordmark";
import { Copyright } from "./copyright";
import { initials, titleCase } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { useGating, type Gate } from "@/lib/gating";
import { useActiveModule, useModuleLink } from "@/lib/module-rail";
import { ModuleSwitchModal } from "./module-switch";
import { BrandMark } from "./brand-mark";
import { Padlock } from "./padlock";
import { Spinner } from "./ui";
import {
  IconAlert,
  IconBank,
  IconBuilding,
  IconCard,
  IconLifebuoy,
  IconLock,
  IconChart,
  IconChevronDown,
  IconClipboardCheck,
  IconClose,
  IconContract,
  IconDashboard,
  IconDoor,
  IconInvoice,
  IconFlag,
  IconLogout,
  IconMail,
  IconMenu,
  IconPayout,
  IconPlus,
  IconReceipt,
  IconSend,
  IconSettings,
  IconSignature,
  IconSheet,
  IconTeam,
  IconKeys,
  IconUsers,
  IconWallet,
  IconWrench,
} from "./icons";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
  count?: number;
  /**
   * A padlock on the item, where the plan does not include what is behind it.
   *
   * **The item is still there and still opens.** Shutting the door and taking the sign down are
   * different things, and this product now only does the first: a rail item nobody can see is a
   * feature nobody buys. Pressing the padlock opens the pricing modal; pressing the item itself
   * opens the screen, which draws its own options padlocked and says the same thing at more
   * length.
   *
   * Attached *after* the permission filter below, never instead of it. See `useRecordLock` in
   * `lib/gating.ts` for why the two are not interchangeable.
   */
  gate?: Gate | null;
  /**
   * Items that live **under** this one, drawn indented and only when the reader is in that area.
   *
   * **This exists because the developer rail carried fifteen items and the operator could not read
   * it.** Their words: *"I have a whole lot of stuff that I can't even tell what is what."* Item 1
   * of `14-DEVELOPMENT-MODULE-PLAN.md` cuts it to eight and moves Bank accounts, Import and export,
   * Team, Billing and Support **under Settings** — which is where somebody looks for them anyway,
   * and none of the five is opened in the course of a day's work.
   *
   * They appear the moment the reader is on Settings or on one of the five, so pressing Settings is
   * the way in and nothing is unreachable. They are collapsed everywhere else, because a rail that
   * is eight items on one screen and thirteen on another is not eight items.
   */
  children?: Array<NavItem & { need?: Permission }>;
}

export function AppLayout() {
  const { loading, user, org, vocab, can, mayReach, modules, restriction, restrictionNotice } = useSession();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  /* The module the reader has asked to move to, held until they confirm or think better of it.
     Null means no question is being asked, which is the ordinary state. */
  const [switchTo, setSwitchTo] = useState<ModuleCode | null>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
    setAccountOpen(false);
    setCreateOpen(false);
  }, [location.pathname, location.search]);

  /*
    Which rail this is.

    **The module follows the address, not a stored preference.** Somebody who opens a link into a
    development property should find the rail has come with them, rather than being left looking at
    a letting rail with nothing on it highlighted. The switcher below changes the address, and the address
    changes the rail — one direction, so the two can never disagree. It also narrows to what the
    organisation actually holds, which is what matters on the screens the two share: a developer
    opening `/app/clients` must get their own rail. ADR-0080.

    **It is a hook, so it lives above the early returns.** It used to be a plain function called
    further down, and moving the remembering into it without moving the call left the component
    running fewer hooks on the render where the session was still loading and more on the next —
    "Rendered more hooks than during the previous render", and a blank screen on every developer
    address. Nothing below this line may call a hook.
  */
  const active: ModuleCode = useActiveModule(modules);

  /*
    The module the shell is skinned as — and it is simply the module in force.

    **It used to be gated on holding both.** The reasoning was that a colour exists to tell two
    things apart, so an account with one module was shown nothing to tell apart and kept the green.
    The operator has decided otherwise, and the decision is better: *"I want that blue branding to be
    specific for development module now."*

    Blue is not a contrast device, it is what the development module **is**. A developer who buys
    only that module should see the same product as a developer inside an agency that runs both —
    same colour, same screenshots, same thing to recognise. Under the old rule the two saw different
    applications, and the one who had bought less saw the letting product's green.

    So: `active`, always. Letting resolves to no skin, which is the brand's own green and is what it
    has always been. ADR-0166.
  */
  const railModule = active;

  /*
    And the same value on the root element, which is what makes a **reload** blue from the first
    frame rather than green-then-blue.

    `main.tsx` puts a guess there before React starts, from the path and what the tab remembered.
    This is the correction: it runs once the session is in, so an account that turns out not to hold
    the development module drops back to green after one fetch instead of keeping a skin it has no
    right to. It is also what keeps the root honest as somebody moves between the two modules
    without reloading.

    On `<html>`, not on the shell below, because a dialog or a toast portalled out of the React tree
    is not inside the shell — and a blue application with a green dialog on top of it is precisely
    the sort of thing nobody notices until a customer does.
  */
  useEffect(() => {
    /*
      **Not while the session is still loading.** `modules` defaults to `["letting"]` until the
      profile lands, so `active` narrows to letting for those few hundred milliseconds — and an
      unguarded version of this effect deleted the attribute `main.tsx` had just set. Measured: blue
      at 40ms, green from 120ms, which is the reported flash with an extra step in it. The guess
      stands until there is something better than a guess to replace it with.
    */
    if (loading) return;
    const root = document.documentElement;
    if (railModule && railModule !== "letting") root.dataset.module = railModule;
    else delete root.dataset.module;
  }, [loading, railModule]);

  /*
    Every shut door, as the server decided it — see `lib/gating.ts`.

    A hook, so it lives above the early returns with `useActiveModule`. It is null until the
    settings payload lands, which is deliberately the same answer as "nothing is gated": a rail
    that grew a padlock a second after it was drawn would be worse than one that never drew it.
  */
  const gating = useGating();

  const { data: counts } = useQuery({
    queryKey: ["nav-counts"],
    queryFn: async () => {
      const data = await api.get<{ money: { overdue: { count: number } }; overdueMaintenance: number }>("/dashboard");
      return { overdue: data.money.overdue.count, maintenance: data.overdueMaintenance };
    },
    /* Letting figures. A development-only organisation has no overdue invoices and no
       maintenance, and asking for them would be one request per page load for two zeroes. */
    enabled: Boolean(user && org) && modules.includes("letting"),
    staleTime: 60_000,
  });

  if (loading) return <Spinner label="Loading your agency…" />;
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  if (!org) return <Navigate to="/sign-up" replace />;

  /*
    The rail is what this person can actually open.

    Every item names the permission it needs and is dropped without it — an accountant has no
    reason to see a "Team" link that answers "you do not have permission to do that", and a viewer
    has no reason to be shown five screens they cannot reach. A group with nothing left in it goes
    too, heading and all.

    This is not the security boundary. The API refuses regardless, and `Require` sends anybody who
    types the address to a screen they can use; this is about not offering what cannot be had.
  */

  /**
   * The developer rail: **nine items, in the operator's own order.**
   *
   * It carried fifteen, and five of those — Purchases, Reservations, Buyers, Marketers, Payments —
   * were five doors onto one thing: selling a unit to somebody. That is what produced *"I have a
   * whole lot of stuff that I can't even tell what is what"*, and item 1 of
   * `14-DEVELOPMENT-MODULE-PLAN.md` settles the replacement:
   *
   * > Overview · Properties · Sales · Buyers · Reports · Marketers · Payments · Settings
   *
   * **Reservations stops being a place.** A reservation is a sale that has not converted, so it is
   * a *state* inside Sales rather than a screen of its own — one flow with stages, item 5. The
   * records did not move and neither did their addresses; what moved is where the reader stands.
   *
   * **No group headings, deliberately.** Four headings over nine items is a heading for every two,
   * which is furniture rather than structure. The letting rail keeps its four groups because it
   * genuinely has twenty items to sort.
   */
  const developing: Array<{ title: string | null; items: Array<NavItem & { need?: Permission }> }> = [
    {
      title: null,
      items: [
        { to: "/app/development", label: "Overview", icon: <IconDashboard />, end: true, need: "property.view" },
        /* "Properties", not "Estates".
           A developer calls the whole scheme a property just as a letting agency calls a building
           one, and the rail is read by somebody who does one of the two jobs rather than by
           somebody comparing the vocabularies. Which is why the address is
           `/app/development/properties` rather than `/app/properties`: the same word now names a
           record on each side of the business, and only the prefix can say which is meant. The
           record is still an estate in the database and in the API — renaming tables to match a
           label is churn nobody can see — and `/app/estates` still redirects here, because it has
           been sent to people. */
        { to: "/app/development/properties", label: "Properties", icon: <IconBuilding />, need: "property.view" },
        /* One item where there were two, and no `?stage=` on it.
           A rail item that arrives pre-filtered is a rail item that hides the rest of the list from
           anybody who does not notice the chip — "where are the ones I have sold?" was the result
           last time. The screen carries its own chips, and every one of them has an address an
           Overview figure can link to. */
        { to: "/app/development/sales", label: "Sales", icon: <IconContract />, need: "lease.view" },
        /* Buyers, and not the shared client list under a different label — which is what it was,
           and why it read as nothing. A buyer is a client with a purchase; somebody who has only
           enquired is a client and belongs on the client list. Item 13. */
        { to: "/app/development/buyers", label: "Buyers", icon: <IconUsers />, need: "party.view" },
        { to: "/app/reports", label: "Reports", icon: <IconChart />, need: "report.view" },
        /* A marketer is somebody the agency owes — earned, paid and owed is the whole screen — so
           `payment.view` rather than a portfolio permission. */
        { to: "/app/development/marketers", label: "Marketers", icon: <IconUsers />, need: "payment.view" },
        { to: "/app/payments", label: "Payments", icon: <IconReceipt />, need: "payment.view" },
        /* Costs, which the development rail did without for as long as a cost could only be spent
           on a letting building. `expenses.module` has always been stamped, so the records and the
           scoping were already right; what was missing was somewhere to put the scheme, and with
           `expenses.estate_id` there now is. The screen is shared and answers for the module in
           force — the picker lists schemes here and buildings there. ADR-0161. */
        { to: "/app/expenses", label: "Expenses", icon: <IconWallet />, need: "expense.view" },
        /* The ninth, and the one that holds the five that were moved off the rail. */
        {
          to: "/app/settings",
          label: "Settings",
          icon: <IconSettings />,
          need: "org.settings",
          children: [
            { to: "/app/bank-accounts", label: "Bank accounts", icon: <IconBank />, need: "org.settings" },
            /* Padlocked rather than removed on a plan that does not include it — ADR-0101. The
               screen it opens draws every list padlocked too, so the two agree wherever somebody
               arrives. */
            { to: "/app/data", label: "Import and export", icon: <IconSheet />, need: "report.view", gate: gating?.features?.data_transfer ?? null },
            { to: "/app/settings/team", label: "Team", icon: <IconTeam />, need: "org.members" },
            { to: "/app/settings/billing", label: "Billing", icon: <IconCard />, need: "org.billing" },
            { to: "/app/support", label: "Support", icon: <IconLifebuoy /> },
          ],
        },
      ],
    },
  ];

  const letting: Array<{ title: string | null; items: Array<NavItem & { need?: Permission }> }> = [
    {
      title: "Today",
      items: [
        { to: "/app", label: "Dashboard", icon: <IconDashboard />, end: true },
        { to: "/app/invoices?bucket=overdue", label: "Overdue", icon: <IconAlert />, count: counts?.overdue, need: "invoice.view" },
        { to: "/app/reminders", label: "Reminders", icon: <IconSend />, need: "reminder.send" },
        { to: "/app/getting-started", label: "Getting started", icon: <IconFlag /> },
      ],
    },
    {
      title: "Portfolio",
      items: [
        { to: "/app/properties", label: titleCase(vocab.properties), icon: <IconBuilding />, need: "property.view" },
        { to: "/app/units", label: titleCase(vocab.units), icon: <IconDoor />, need: "property.view" },
        { to: "/app/tenancies", label: titleCase(vocab.leases), icon: <IconContract />, need: "lease.view" },
        /* Beside the tenancies rather than under Operations, because that is what an agreement is
           about and where somebody looking for one will look. `lease.view`, so it is offered to
           exactly the people who can open the tenancy it came from. */
        { to: "/app/agreements", label: "Agreements", icon: <IconSignature />, need: "lease.view" },
        { to: "/app/clients", label: "Clients", icon: <IconUsers />, need: "party.view" },
        { to: "/app/enquiries", label: "Listing enquiries", icon: <IconSend />, need: "property.edit" },
      ],
    },
    {
      title: "Money",
      items: [
        { to: "/app/invoices", label: titleCase(vocab.invoices), icon: <IconInvoice />, need: "invoice.view" },
        { to: "/app/payments", label: "Payments", icon: <IconReceipt />, need: "payment.view" },
        { to: "/app/expenses", label: "Expenses", icon: <IconWallet />, need: "expense.view" },
        { to: "/app/bank-accounts", label: "Bank accounts", icon: <IconBank />, need: "org.settings" },
        { to: "/app/reports", label: "Reports", icon: <IconChart />, need: "report.view" },
        { to: "/app/payouts", label: "Landlord payouts", icon: <IconPayout />, need: "payout.view" },
        { to: "/app/settlements", label: "Settlements", icon: <IconWallet />, need: "payout.view" },
      ],
    },
    {
      title: "Operations",
      items: [
        { to: "/app/maintenance", label: "Maintenance", icon: <IconWrench />, count: counts?.maintenance, need: "maintenance.view" },
        { to: "/app/inspections", label: "Inspections", icon: <IconClipboardCheck />, need: "maintenance.view" },
        /* Padlocked rather than removed on a plan that does not include it — ADR-0101. The screen
           it opens draws every list padlocked too, so the two agree wherever somebody arrives. */
        { to: "/app/data", label: "Import and export", icon: <IconSheet />, need: "report.view", gate: gating?.features?.data_transfer ?? null },
        { to: "/app/settings/team", label: "Team", icon: <IconTeam />, need: "org.members" },
        { to: "/app/settings/billing", label: "Billing", icon: <IconCard />, need: "org.billing" },
        { to: "/app/settings", label: "Settings", icon: <IconSettings />, need: "org.settings" },
        { to: "/app/support", label: "Support", icon: <IconLifebuoy /> },
      ],
    },
  ];

  /* `mayReach`, not `can`: a suspension is read-only, not a smaller application. Several of these
     screens are gated by an edit permission because that is who has business opening them, not
     because opening them writes anything — and hiding Team from a suspended owner would take away
     the screen that shows them what has happened. */
  /*
    Every rail link carries the module it was pressed from, where the screen belongs to both.

    Without it, pressing Payments from the development rail landed on a shared screen that answered
    "letting" — and the whole navigation flipped underneath somebody who had not asked to leave.
    `withModule` leaves alone anything one module owns (the path already says) and anything for an
    organisation with a single module (nothing to be confused about), so this adds a parameter only
    where one is genuinely needed.
  */
  /* The same filter and the same module parameter, applied to a child as to its parent — a nested
     item is a rail item in every respect except where it is drawn, and one that skipped either rule
     would offer Team to an accountant or flip the rail to letting when pressed. */
  const dressItem = (item: NavItem & { need?: Permission }) => ({
    ...item,
    to: withModule(item.to, active, modules),
    children: item.children
      ?.filter((child) => !child.need || mayReach(child.need))
      .map((child) => ({ ...child, to: withModule(child.to, active, modules) })),
  });

  const groups = (active === "developing" ? developing : letting)
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.need || mayReach(item.need)).map(dressItem),
    }))
    .filter((group) => group.items.length > 0);

  /**
   * Which item is highlighted.
   *
   * React Router's own `isActive` ignores the query string, so on `/app/invoices?bucket=overdue`
   * both "Overdue" and "Invoices" lit up at once. The rule instead:
   *
   * - an item whose full target (path *and* query) matches the current location wins outright,
   *   and nothing else is highlighted;
   * - otherwise items that carry no query fall back to matching on the path.
   *
   * So the filtered view highlights the filter, the unfiltered list highlights the list, and a
   * detail page under `/app/invoices/…` still highlights Invoices.
   */
  /*
    The module parameter is navigation state, not a filter, so it is stripped before anything is
    compared. Leaving it in made every shared rail item "a filtered item", which only ever matches
    exactly — so Payments stopped highlighting the moment somebody opened a payment.
  */
  const withoutModule = (value: string) => {
    const [path, query = ""] = value.split("?");
    const params = new URLSearchParams(query);
    params.delete("module");
    const rest = params.toString();
    return rest ? `${path}?${rest}` : path;
  };

  const here = withoutModule(location.pathname + location.search);
  /* Children are part of the same competition. `/app/settings/team` is under both Settings and
     Team, and the answer must still be Team — leaving the nested items out of this set would light
     their parent instead, and the reader would be told they are on a screen they are not on. */
  const allItems = groups.flatMap((group) => group.items.flatMap((item) => [item, ...(item.children ?? [])]));
  const exact = allItems.find((item) => withoutModule(item.to) === here);

  /*
    Whatever inner page somebody is on, the rail item it belongs under is lit.

    **The longest matching item wins, and only that one.** `/app/settings/team` is under both
    "Settings" and "Team"; the answer a person expects is Team, because it is the more specific of
    the two and it is the one they pressed. `/app/settings/api-keys` has no item of its own, so the
    longest match is "Settings" and that is what lights — which is the whole of the request. A rule
    that lit every prefix would put two and sometimes three items on at once and stop meaning
    anything.

    `end` still means "exactly this and nothing under it", and it is now carried by only the two
    module homes. `/app` is a prefix of every address in the letting side of the product, so without
    it the Dashboard would be lit on every screen there is.

    A filtered item — one with a query on it, like Overdue — matches exactly or not at all. Two
    items pointing at the same list, one of them narrowed, cannot both be the answer, and the
    narrowed one is only right when the address carries the narrowing.
  */
  const candidates = allItems
    .filter((item) => !item.end && !withoutModule(item.to).includes("?"))
    .map((item) => ({ item, path: withoutModule(item.to) }))
    .filter(({ path }) => location.pathname === path || location.pathname.startsWith(`${path}/`))
    .sort((a, b) => b.path.length - a.path.length);
  const nearest = candidates[0]?.item ?? null;

  /**
   * Compared by **address**, never by object identity or by the raw string.
   *
   * **This is a robustness change, not a fix for a reported fault.** The operator reported that the
   * Properties tab did not light on a phone; that was never reproduced — pressing every tab, in
   * both modules, at 390px, lights exactly one and the right one, with these comparisons and
   * without them. Whatever they saw is still unexplained, and this comment must not be read as
   * having explained it.
   *
   * What is genuinely wrong with the old form, and worth correcting on its own:
   *
   * - `nearest === item` compared **object identity**, and the phone's tab bar builds its own
   *   array. A tab could never *be* a rail item, so that branch could never light one. It happened
   *   not to matter because `exact` answers first for every address a tab points at.
   * - `item.to === exact.to` compared the strings **as written**. A tab's address goes through
   *   `withModule`, so a shared destination carries `?module=`; the rail item it is compared with
   *   does not. Two spellings of the same screen.
   *
   * Both are latent: they are wrong in a way that shows only when a caller passes items from
   * somewhere other than `groups`, which is exactly what the tab bar does. Normalising both sides
   * keeps what the identity check was protecting — `nearest` is still the single longest match, so
   * only one item lights.
   */
  const isActive = (item: NavItem): boolean => {
    const path = withoutModule(item.to);
    if (exact) return path === withoutModule(exact.to);
    if (path.includes("?")) return false;
    if (item.end) return location.pathname === path;
    return nearest !== null && withoutModule(nearest.to) === path;
  };

  return (
    /* The module skins the whole shell, not just the rail.
       Everything below reads its colour from `--color-brand-*` or `--ds-accent*`, whether it is a
       Tailwind utility or a shared component — so redefining those under this one attribute turns
       every button, link, badge and focus ring with it. Scoped here rather than on `<html>` because
       the sign-in screen and the tenant portal are not inside a module and must stay the brand's. */
    <div className="min-h-screen bg-ink-50" data-module={railModule}>
      {/* The rail. Fixed on desktop, a drawer on a phone. */}
      <aside
        className={`sidebar fixed inset-y-0 left-0 z-40 flex flex-col overflow-y-auto transition-transform lg:translate-x-0 ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-2.5 px-6 py-5">
          {/* The mark is the way home, as it is in every other application. Closing the drawer
              on the way keeps the phone from landing on the dashboard behind an open menu. */}
          <Link
            /* This module's home, not the product's. The mark is the way home from anywhere, and
               sending a developer to the letting dashboard is the crossing the operator reported. */
            to={moduleHome(active)}
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-2.5"
            aria-label="Dashboard"
          >
            {/* White, because the rail is dark in every skin. Drawn inline rather than fetched, so
                there is one drawing rather than one per ground — see `brand-mark.tsx`. */}
            <BrandMark size={30} tone="light" />
            <Wordmark tone="light" className="text-lg" />
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="ml-auto text-white/60 hover:text-white lg:hidden"
            aria-label="Close menu"
          >
            <IconClose />
          </button>
        </div>

        {/*
          The switcher.

          **Two rails and a switch, not one rail that grows.** The two are different businesses —
          a tenancy and a purchase have almost nothing in common — and a single rail carrying both
          would be a list of fourteen things where nobody could see either job. It is drawn only
          for an organisation that actually has both: a letting agency should never learn that a
          developer's module exists by finding a dead switch above their navigation.
        */}
        {modules.length > 1 ? (
          <div className="mx-3 mb-4 flex rounded-[var(--radius-control)] bg-white/10 p-1" role="tablist" aria-label="Module">
            {MODULES.filter((code) => modules.includes(code)).map((code) => (
              <Link
                key={code}
                /* `moduleHome`, not `MODULE_INFO[code].home`: letting's home is `/app`, which no
                   module owns, so a bare link there let the rail fall back to whatever was
                   remembered and the switch did nothing. See `moduleHome` in @rentorng/core. */
                to={moduleHome(code)}
                /* Still a real link — the address is right, middle-click and "open in new tab" work,
                   and a reader is told where it goes. The dialog intercepts the ordinary press only;
                   changing module is not navigation and is worth a sentence first. Pressing the tab
                   you are already on is not a switch and asks nothing. */
                onClick={(event) => {
                  if (code === active) return;
                  event.preventDefault();
                  setSwitchTo(code);
                }}
                role="tab"
                aria-selected={active === code}
                title={MODULE_INFO[code].blurb}
                className={`flex-1 rounded-[calc(var(--radius-control)-2px)] px-3 py-2.5 text-center text-sm font-semibold transition-colors ${
                  active === code ? "bg-white text-brand-800" : "text-white/70 hover:text-white"
                }`}
              >
                {MODULE_INFO[code].label}
              </Link>
            ))}
          </div>
        ) : null}

        <nav className="flex-1 px-3 pb-6">
          {groups.map((group, index) => (
            /* Keyed by the title where there is one, and by position where there is not. The
               developer rail is a single headless group of nine — four headings over nine items
               is a heading for every two, which is furniture rather than structure. */
            <div key={group.title ?? `group-${index}`} className="mb-6">
              {group.title ? <p className="nav-heading">{group.title}</p> : null}
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  /*
                    Whether this item's children are showing.

                    Open when the reader is on the parent or on one of the children — so pressing
                    Settings is the way in and nothing is unreachable, and the rail is eight items
                    everywhere else. Derived from where they are rather than held in state, so it
                    cannot be left open on a screen it has nothing to do with.
                  */
                  const open =
                    item.children?.length &&
                    (isActive(item) || item.children.some((child) => isActive(child)));
                  return (
                    <li key={item.to}>
                      <NavLink to={item.to} end={item.end} className={`nav-item ${isActive(item) ? "nav-item-active" : ""}`}>
                        <span className="shrink-0">{item.icon}</span>
                        <span className="truncate">{item.label}</span>
                        {item.count ? <span className="nav-count">{item.count}</span> : null}
                        {/* Inside the link on purpose: the padlock belongs to the item it marks, and
                            it stops the press reaching the link so a padlock never navigates. */}
                        {item.gate ? <Padlock gate={item.gate} tone="rail" className="ml-auto" /> : null}
                      </NavLink>
                      {open ? (
                        <ul className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
                          {item.children!.map((child) => (
                            <li key={child.to}>
                              <NavLink
                                to={child.to}
                                end={child.end}
                                className={`nav-item ${isActive(child) ? "nav-item-active" : ""}`}
                              >
                                <span className="shrink-0">{child.icon}</span>
                                <span className="truncate">{child.label}</span>
                                {child.gate ? <Padlock gate={child.gate} tone="rail" className="ml-auto" /> : null}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <ModuleAdvert gates={gating?.modules} />

        <div className="border-t border-white/10 px-6 py-4">
          <p className="truncate text-sm font-medium text-white">{org.name}</p>
          {/*
            Deliberately not `{org.currency} · {org.timezone}`.

            It read as "NGN · Africa/Lagos" — a currency code and a continent, in the one piece of
            chrome on every screen in the product. The standing instruction is that no user-facing
            copy names a continent, a country or a currency; that these came from the agency's own
            settings is not a defence, because a reader cannot tell the difference between the
            product saying it and the product repeating it back.

            What is actually useful under the agency's name is which account somebody is signed in
            as, which is the thing people get wrong when they hold two.
          */}
          <p className="truncate text-xs text-white/50">{user?.email}</p>
          <Copyright tone="light" className="mt-2" />
        </div>
      </aside>

      {menuOpen ? (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          onClick={() => setMenuOpen(false)}
          data-no-ripple
          className="fixed inset-0 z-30 bg-ink-900/40 lg:hidden"
        />
      ) : null}

      <div className="lg:pl-64">
        <header className="app-bar sticky top-0 z-20 border-b border-ink-200 bg-white">
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
            {/* The rail opens from the header on a phone. It was a tab, which spent one of five
                slots on a menu; up here it is where a menu is looked for, and the slot went back
                to being somewhere to go. */}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="More"
              className="-ml-1 flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] text-ink-600 hover:bg-ink-100 hover:text-ink-900 lg:hidden"
            >
              <IconMenu size={22} />
            </button>

            {/* The phone's header, on a light ground. The mark takes the skin's colour so it
                matches the wordmark beside it — that is text, so it always followed the skin, and
                a fixed green mark next to a blue word is the one thing a logo may never be. */}
            <Link to={moduleHome(active)} className="flex items-center gap-2 lg:hidden" aria-label="Dashboard">
              <BrandMark size={28} />
              <Wordmark className="text-base" />
            </Link>

            <div className="ml-auto flex items-center gap-2">
              {/*
                The shortcuts follow the module, as the rail does.

                Offering "New tenancy" while somebody is looking at a development property is the
                wrong half of the business — it was there in the first screenshot of the developer module
                and read as the rail having failed to switch. Both are still gated by the
                permission, as everything else is.
              */}
              {active === "developing" ? (
                can("lease.edit") ? (
                  <Link to="/app/development/sales/new" className="btn btn-primary btn-sm hidden md:inline-flex">
                    <IconPlus size={16} />
                    Record a sale
                  </Link>
                ) : null
              ) : (
                <>
                  {/* Offered only to somebody who can actually do it — see `Require`. */}
                  {can("lease.edit") ? (
                    <Link to="/app/tenancies/new" className="btn btn-secondary btn-sm hidden md:inline-flex">
                      <IconPlus size={16} />
                      New {vocab.lease}
                    </Link>
                  ) : null}
                  {/* The phone has the centre button for this; a second copy up here is the same
                      action twice on a screen with no room for it. */}
                  {can("payment.record") ? (
                    <Link to="/app/payments/new" className="btn btn-primary btn-sm hidden lg:inline-flex">
                      <IconPlus size={16} />
                      Record a payment
                    </Link>
                  ) : null}
                </>
              )}
              <AccountMenu open={accountOpen} onToggle={() => setAccountOpen((v) => !v)} />
            </div>
          </div>
        </header>

        {/* Outside the header, and outside the rail, because it belongs to neither: the switcher
            can be pressed from the rail on a desktop or from the drawer on a phone, and one dialog
            serves both. */}
        <ModuleSwitchModal from={active} to={switchTo} onClose={() => setSwitchTo(null)} />

        <main className="has-tabbar mx-auto max-w-[1400px] px-4 pt-6 sm:px-6 sm:pt-8">
          {/* Above the plan banner: a suspension outranks a trial ending. */}
          <RestrictionBanner />
          <ConfirmEmailBanner />
          <PlanBanner />
          <PageTransition>
            <Outlet />
          </PageTransition>
        </main>
      </div>

      <TabBar
        vocab={vocab}
        module={active}
        modules={modules}
        overdue={counts?.overdue}
        isActive={isActive}
        onCreate={() => setCreateOpen(true)}
      />

      {createOpen ? <CreateSheet vocab={vocab} module={active} modules={modules} onClose={() => setCreateOpen(false)} /> : null}
    </div>
  );
}

/**
 * The bottom bar.
 *
 * Five slots and no more. The first four are where the work is — today, the buildings, the money,
 * the people — and the fifth opens the full rail, so nothing is unreachable and nothing is
 * crammed in. The overdue count rides on the money tab for the same reason it rides on the rail:
 * an arrear should never be somewhere you have to go and look for.
 */
function TabBar({
  vocab,
  module,
  modules,
  overdue,
  isActive,
  onCreate,
}: {
  vocab: Vocabulary;
  /** The phone gets the module it is in, not the module the product used to be. */
  module: ModuleCode;
  modules: ModuleCode[];
  overdue?: number;
  isActive: (item: NavItem) => boolean;
  onCreate: () => void;
}) {
  /**
   * Four destinations and the centre button.
   *
   * "More" used to sit in the fifth slot, which spent a whole tab on a menu — so it moved up to
   * the header, where a menu is expected, and the slot went to the work. Clients are behind the
   * rail, because this app is opened to find out who has not paid far more often than to look
   * somebody up.
   *
   * **The developer module gets its own four.** The customer asked for this to be easy to use at a
   * site office on a phone, and four tabs to a letting portfolio a developer does not have is the
   * opposite of that.
   */
  const raw: NavItem[] =
    module === "developing"
      ? [
          { to: "/app/development", label: "Home", icon: <IconDashboard size={21} />, end: true },
          { to: "/app/development/properties", label: "Properties", icon: <IconBuilding size={21} /> },
          { to: "/app/development/sales", label: "Sales", icon: <IconContract size={21} /> },
          { to: "/app/payments", label: "Payments", icon: <IconReceipt size={21} /> },
        ]
      : [
          { to: "/app", label: "Home", icon: <IconDashboard size={21} />, end: true },
          { to: "/app/properties", label: titleCase(vocab.properties), icon: <IconBuilding size={21} /> },
          { to: "/app/invoices", label: titleCase(vocab.invoices), icon: <IconInvoice size={21} />, count: overdue },
          { to: "/app/tenancies", label: titleCase(vocab.leases), icon: <IconContract size={21} /> },
        ];

  // The same rule as the rail: a shared destination keeps the module it was pressed from.
  const tabs = raw.map((tab) => ({ ...tab, to: withModule(tab.to, module, modules) }));

  return (
    <nav className="tabbar lg:hidden" aria-label="Sections">
      {tabs.slice(0, 2).map((tab) => (
        <Tab key={tab.to} tab={tab} active={isActive(tab)} />
      ))}

      <button type="button" onClick={onCreate} className="tab-action" aria-label="Add something">
        <IconPlus size={24} />
      </button>

      {tabs.slice(2).map((tab) => (
        <Tab key={tab.to} tab={tab} active={isActive(tab)} />
      ))}
    </nav>
  );
}

function Tab({ tab, active }: { tab: NavItem; active: boolean }) {
  return (
    <NavLink to={tab.to} end={tab.end} className={`tab-item ${active ? "tab-item-active" : ""}`}>
      {tab.icon}
      <span className="max-w-full truncate px-1">{tab.label}</span>
      {tab.count ? <span className="tab-count">{tab.count > 99 ? "99+" : tab.count}</span> : null}
    </NavLink>
  );
}

/**
 * What the centre button opens.
 *
 * Four things, in the order they happen: money arrives, a tenancy is signed, a building is taken
 * on, somebody new is met. A menu of everything the app can create would be the rail again, and
 * the rail already exists one tab to the right.
 */
function CreateSheet({
  vocab,
  module,
  modules,
  onClose,
}: {
  vocab: Vocabulary;
  /** What this person is in the middle of. The four things offered are that module's four. */
  module: ModuleCode;
  modules: ModuleCode[];
  onClose: () => void;
}) {
  const { can } = useSession();

  /* The same rule as the rail and the header: what this person cannot do is not offered — and
     what their account does not include is not offered either. The centre button is the one
     control on a phone that says what this product is *for*, so pointing it at the wrong
     business is worse here than anywhere. */
  const all =
    module === "developing"
      ? [
          { to: "/app/development/sales/new", label: "Record a sale", icon: <IconContract size={19} />, need: "lease.edit" as const },
          { to: "/app/development/properties/new", label: "Add a property", icon: <IconBuilding size={19} />, need: "property.edit" as const },
          /* **The list, not a form, and the label says so.**

             What is inside a scheme cannot exist without one — a plot has to be added to a property,
             and a run of fifty is created in one step on that property's own page rather than
             through fifty forms. So there is no standalone "new plot" screen to point at, and
             inventing one would mean asking which scheme first, which is what the list already
             does. "Add units to a property" is two steps and admits it; "Add a unit" would promise a
             form that does not exist.

             The noun is generic on purpose: a scheme decides whether its contents are plots, homes
             or units (`soldNoun`), and this sheet is drawn above every scheme at once. */
          { to: "/app/development/properties", label: "Add units to a property", icon: <IconDoor size={19} />, need: "property.edit" as const },
          { to: "/app/clients/new", label: "Add a buyer", icon: <IconUsers size={19} />, need: "party.edit" as const },
        ]
      : [
          { to: "/app/payments/new", label: "Record a payment", icon: <IconReceipt size={19} />, need: "payment.record" as const },
          { to: "/app/tenancies/new", label: `New ${vocab.lease}`, icon: <IconContract size={19} />, need: "lease.edit" as const },
          { to: "/app/properties/new", label: `Add a ${vocab.property}`, icon: <IconBuilding size={19} />, need: "property.edit" as const },
          /* A real form on this side: `/app/units/new` asks which building it belongs to, so a unit
             can be added without opening the building first. It was missing from the one control on
             a phone that says what this product is for, while the building above it was here. */
          { to: "/app/units/new", label: `Add a ${vocab.unit}`, icon: <IconDoor size={19} />, need: "property.edit" as const },
          { to: "/app/clients/new", label: "Add a client", icon: <IconUsers size={19} />, need: "party.edit" as const },
        ];

  const actions = all
    .filter((action) => can(action.need))
    .map((action) => ({ ...action, to: withModule(action.to, module, modules) }));

  return (
    <>
      <button
        type="button"
        aria-label="Close"
        data-no-ripple
        onClick={onClose}
        className="fixed inset-0 z-40 bg-ink-900/40 lg:hidden"
      />
      <div className="sheet lg:hidden" role="dialog" aria-label="Add something">
        <div className="sheet-grip" />
        <div className="py-1">
          {actions.map((action) => (
            <Link key={action.to} to={action.to} onClick={onClose} className="sheet-item">
              <span className="sheet-glyph">{action.icon}</span>
              {action.label}
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}

function AccountMenu({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { user, org, orgs, role, consoleUrl, refresh, modules } = useSession();
  const navigate = useNavigate();
  /*
    Every one of the four destinations below is a screen **both** modules share, and this menu is
    drawn on every screen in the product. Without the module on them, opening "Settings" from the
    development rail in a new tab — or after a reload, or from a pasted address — arrives on an
    address with nothing in it to say which business is meant, and the rail falls back to the first
    module the account holds, which for an account holding both is always letting. That is the
    crossing the operator reported, in the one control that is never off the screen.
  */
  const link = useModuleLink(modules);

  const signOut = useApiMutation({
    mutationFn: () => api.post("/auth/sign-out"),
    invalidates: [["*"]],
    onSuccess: () => navigate("/sign-in", { replace: true }),
  });

  const switchOrg = useApiMutation({
    mutationFn: (orgId: string) => api.post("/auth/switch-org", { orgId }),
    invalidates: [["*"]],
    onSuccess: async () => {
      await refresh();
      navigate("/app");
    },
  });

  if (!user) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-ink-200 py-1 pl-1 pr-2 transition-colors hover:bg-ink-50"
      >
        <span className="flex size-8 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
          {initials(user.name)}
        </span>
        <span className="hidden text-sm font-medium sm:inline">{user.name.split(" ")[0]}</span>
        <IconChevronDown size={16} className="text-ink-400" />
      </button>

      {open ? (
        <>
          {/* Closes the menu when you click away. `data-no-ripple` because it covers the whole
              viewport, and a press on it is a press on nothing in particular. */}
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            data-no-ripple
            onClick={onToggle}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div
            className="absolute right-0 z-20 mt-2 w-64 overflow-hidden rounded-[var(--radius-surface)] border border-ink-200 bg-white p-1.5"
            style={{ boxShadow: "var(--shadow-menu)" }}
          >
            <div className="border-b border-ink-100 px-3 py-2.5">
              <p className="text-sm font-semibold">{user.name}</p>
              <p className="truncate text-xs text-ink-500">{user.email}</p>
              {org ? <p className="mt-1 text-xs capitalize text-ink-500">{role} · {org.name}</p> : null}
            </div>

            {orgs.length > 1 ? (
              <div className="border-b border-ink-100 py-1.5">
                <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-400">Switch agency</p>
                {orgs.map((membership) => (
                  <button
                    key={membership.id}
                    type="button"
                    onClick={() => switchOrg.mutate(membership.id)}
                    className={`block w-full rounded-[var(--radius-control)] px-3 py-1.5 text-left text-sm hover:bg-ink-100 ${
                      membership.id === org?.id ? "font-semibold text-brand-700" : ""
                    }`}
                  >
                    {membership.name}
                  </button>
                ))}
              </div>
            ) : null}

            <Link to={link("/app/settings")} className="block rounded-[var(--radius-control)] px-3 py-2 text-sm hover:bg-ink-100">
              Settings
            </Link>
            <Link to={link("/app/settings/security")} className="block rounded-[var(--radius-control)] px-3 py-2 text-sm hover:bg-ink-100">
              Your security
            </Link>
            <Link to={link("/app/settings/team")} className="block rounded-[var(--radius-control)] px-3 py-2 text-sm hover:bg-ink-100">
              Team
            </Link>
            <Link to={link("/app/settings/billing")} className="block rounded-[var(--radius-control)] px-3 py-2 text-sm hover:bg-ink-100">
              Plan and billing
            </Link>
            {/* Staff only, and the server decides who that is — `consoleUrl` is simply absent for
                everybody else, so this is not a hidden link somebody can find in the bundle.
                It opens the console's own sign-in form: holding this session does not grant that
                one, which is the whole point of the boundary. */}
            {consoleUrl ? (
              <a
                href={consoleUrl}
                target="_blank"
                rel="noreferrer"
                className="block rounded-[var(--radius-control)] px-3 py-2 text-sm hover:bg-ink-100"
              >
                Platform console ↗
              </a>
            ) : null}
            <button
              type="button"
              onClick={() => signOut.mutate(undefined as never)}
              className="flex w-full items-center gap-2 rounded-[var(--radius-control)] px-3 py-2 text-left text-sm text-danger-600 hover:bg-danger-50"
            >
              <IconLogout size={16} />
              Sign out
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** At most one banner, so it keeps its force. The API decides which one, if any. */
/**
 * What a suspension looks like from the inside.
 *
 * **It is above everything, on every screen, and it cannot be dismissed.** A read-only account
 * whose notice can be closed is an account where somebody presses a button, watches it fail, and
 * rings us — the whole cost of a suspension is support calls, and this is what prevents them.
 *
 * The wording is the server's, not the app's: `describeRestriction()` in `@rentorng/core` decides
 * what a suspended person is told, so the sign-in refusal, the API's refusals and this banner all
 * say the same thing. Hard-coding a sentence here would be a fourth version of it.
 */
function RestrictionBanner() {
  const { restriction, restrictionNotice } = useSession();
  if (restriction.level === "none" || !restrictionNotice) return null;

  return (
    <div
      role="status"
      className={`mb-6 rounded-[var(--radius-surface)] border px-4 py-3 text-sm ${
        restriction.level === "hard"
          ? "border-danger-200 bg-danger-50 text-danger-700"
          : "border-warn-200 bg-warn-50 text-warn-700"
      }`}
    >
      <p className="font-semibold">{restrictionNotice.headline}</p>
      <p className="mt-0.5">{restrictionNotice.detail}</p>
    </div>
  );
}

/**
 * "Confirm your email address."
 *
 * Shown until somebody in the agency has confirmed one, because until then nothing the agency
 * queues will go out — see `anyoneConfirmed` in the API and ADR-0085. It says *that*, rather than
 * a vague request to check your inbox, because a banner whose consequence is unstated is a banner
 * people dismiss.
 *
 * Not a modal and not a block. Somebody who mistyped their address needs to get in and change it,
 * and a wall between them and the settings screen is a wall between them and the fix.
 */
function ConfirmEmailBanner() {
  const { user } = useSession();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  if (!user || user.emailVerified) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/verify-email/resend", {});
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div role="status" className="mb-6 flex flex-wrap items-center gap-3 rounded-[var(--radius-surface)] border border-warn-200 bg-warn-50 px-4 py-3 text-sm text-warn-700">
      <IconMail className="size-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">Confirm your email address</p>
        <p className="mt-0.5">
          Reminders and receipts are held until the address on this account is confirmed. Follow the link sent to{" "}
          <span className="font-medium">{user.email}</span>. Nothing is lost in the meantime — held messages are sent
          once it is confirmed.
        </p>
      </div>
      {sent ? (
        <span className="font-medium">Sent — check your inbox.</span>
      ) : (
        <button type="button" onClick={resend} disabled={sending} className="rounded-[var(--radius-control)] border border-warn-300 px-3 py-1.5 font-medium hover:bg-warn-100 disabled:opacity-60">
          {sending ? "Sending…" : "Send it again"}
        </button>
      )}
    </div>
  );
}

/**
 * The one banner across the top of the application, in the tone the server chose.
 *
 * **The tone is the server's, and it is not decoration.** `planBanner` in `apps/api/src/plan.ts`
 * decides which of the three this is, and the third — `locked` — exists because reaching an
 * allowance is not a failure. Nothing is broken, nothing has been taken away and every record goes
 * on being billed; what has happened is that the agency has outgrown its plan, which is the moment
 * the whole feature is built around. Drawn in the danger red that means a real fault everywhere
 * else in the product, it read as an accusation, and a red bar that turns up on an ordinary
 * Tuesday and never leaves is a bar people learn to stop seeing.
 *
 * So it takes the padlock's colours and the padlock's mark — see ADR-0114, and `padlock.tsx`,
 * which is where those colours are decided. The words are untouched: the copy already says what is
 * shut and that the money is still moving, and it is not this file's to soften.
 */
const BANNER_TONE: Record<"warn" | "danger" | "locked", string> = {
  danger: "border-danger-200 bg-danger-50 text-danger-700",
  warn: "border-warn-200 bg-warn-50 text-warn-700",
  locked: "border-brand-200 bg-brand-50 text-brand-800",
};

function PlanBanner() {
  /* Drawn above every screen in both modules, so its one link carries the module it was pressed
     from — see `useModuleLink`. */
  const { modules } = useSession();
  const link = useModuleLink(modules);
  const { data } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () =>
      api.get<{ banner: { tone: "warn" | "danger" | "locked"; title: string; body: string } | null }>("/dashboard"),
    staleTime: 60_000,
  });

  const banner = data?.banner;
  if (!banner) return null;

  return (
    <div
      data-plan-banner={banner.tone}
      className={`plan-banner mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[var(--radius-surface)] border px-4 py-3 text-sm ${
        BANNER_TONE[banner.tone] ?? BANNER_TONE.warn
      }`}
    >
      {/* The same lock the padlocks are drawn with, so the bar at the top of the page and the mark
          beside the row it is talking about are recognisably one thing. */}
      {banner.tone === "locked" ? (
        <span aria-hidden className="grid size-5 shrink-0 place-items-center rounded-full bg-white text-brand-700">
          <IconLock size={12} />
        </span>
      ) : null}
      <span className="font-semibold">{banner.title}</span>
      <span>{banner.body}</span>
      <Link to={link("/app/settings/billing")} className="font-semibold underline">
        See plans
      </Link>
    </div>
  );
}

/**
 * The module this agency does not hold, at the foot of its rail.
 *
 * **Shown, padlocked, and honest about what would open it.** A letting-only agency seeing a shut
 * Development entry is a good advert for a thing it did not know we sold, and the reverse is
 * equally true — this is the one place in the product where somebody finds out the other half
 * exists. ADR-0101 settled that a module not held is a `Gate` for exactly this reason.
 *
 * **It is not a rail item and not a tab, and that is deliberate rather than a compromise.** Both
 * of those mean "somewhere you can go", and this is somewhere nobody in this agency can go by
 * pressing it: `orgs.modules` is a purchase we make on their behalf, so no plan on the pricing
 * table opens it and `needed` is null. Putting an unreachable destination among the reachable ones
 * would teach the reader that half the rail does nothing. Its own heading, below the rail, says
 * what it actually is — the other thing this product does.
 *
 * The words are the server's. `gate.message` already says the module is not part of what the
 * agency holds and that talking to us is what adds it; the modal behind the padlock says the rest.
 */
function ModuleAdvert({ gates }: { gates?: Partial<Record<ModuleCode, Gate>> }) {
  const shut = MODULES.filter((code) => gates?.[code]);
  if (shut.length === 0) return null;

  return (
    <div className="mt-auto px-3 pb-4">
      <p className="nav-heading">Also available</p>
      <ul className="space-y-0.5">
        {shut.map((code) => (
          <li key={code}>
            <div className="nav-item cursor-default gap-3 opacity-70" data-module-advert={code}>
              <span className="shrink-0">{code === "developing" ? <IconBuilding /> : <IconDoor />}</span>
              <span className="truncate">{MODULE_INFO[code].label}</span>
              <Padlock gate={gates?.[code] ?? null} tone="rail" className="ml-auto" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
